import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPlus,
  FiFileText,
  FiRefreshCw,
  FiMail
} from 'react-icons/fi';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { API_URL } from '../../../services/api';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<any>;
  href?: string;
  onClick?: () => void;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo' | 'gray';
  badge?: string | number;
  disabled?: boolean;
}

const QuickActions: React.FC = () => {
  const { token } = useAdminAuth();
  const [pendingClaimsCount, setPendingClaimsCount] = useState<number>(0);
  const [pendingChangesCount, setPendingChangesCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // Fetch pending counts from API
  useEffect(() => {
    const fetchPendingCounts = async () => {
      if (!token) return;

      try {
        

        // Fetch pending ownership claims
        const claimsResponse = await fetch(`${API_URL}/api/admin/claims?status=PENDING&page=1&per_page=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (claimsResponse.ok) {
          const claimsData = await claimsResponse.json();
          setPendingClaimsCount(claimsData.total || claimsData.pending || 0);
        }

        // Fetch pending change requests
        const changesResponse = await fetch(`${API_URL}/api/admin/change-requests/?status=PENDING&page=1&per_page=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (changesResponse.ok) {
          const changesData = await changesResponse.json();
          setPendingChangesCount(changesData.total || 0);
        }
      } catch (error) {
        console.error('Failed to fetch pending counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingCounts();
  }, [token]);

  const quickActions: QuickAction[] = [
    {
      id: 'add_company',
      label: 'เพิ่มบริษัทใหม่',
      description: 'เพิ่มข้อมูลบริษัทรักษาความปลอดภัยใหม่',
      icon: FiPlus,
      href: '/admin/companies/add',
      color: 'blue'
    },
    {
      id: 'pending_requests',
      label: 'อนุมัติคำขอ',
      description: 'ตรวจสอบและอนุมัติคำขอทั้งหมด (Claim, สร้าง, แก้ไข)',
      icon: FiFileText,
      href: '/admin/all-requests',
      color: 'yellow',
      badge: (pendingClaimsCount + pendingChangesCount) > 0 ? (pendingClaimsCount + pendingChangesCount) : undefined
    },
    {
      id: 'view_feedback',
      label: 'ตรวจสอบ Feedback',
      description: 'ดูและตอบกลับ feedback จากผู้ใช้',
      icon: FiMail,
      href: '/admin/feedback',
      color: 'green'
    },
    {
      id: 'view_activities',
      label: 'ดูกิจกรรมล่าสุด',
      description: 'ติดตามการเปลี่ยนแปลงและกิจกรรมในระบบ',
      icon: FiRefreshCw,
      href: '/admin/activities',
      color: 'purple'
    }
  ];

  const getColorClasses = (color: QuickAction['color']) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-500',
        lightBg: 'bg-blue-50',
        text: 'text-blue-600',
        hover: 'hover:bg-blue-100',
        border: 'border-blue-200'
      },
      green: {
        bg: 'bg-green-500',
        lightBg: 'bg-green-50',
        text: 'text-green-600',
        hover: 'hover:bg-green-100',
        border: 'border-green-200'
      },
      yellow: {
        bg: 'bg-yellow-500',
        lightBg: 'bg-yellow-50',
        text: 'text-yellow-600',
        hover: 'hover:bg-yellow-100',
        border: 'border-yellow-200'
      },
      red: {
        bg: 'bg-red-500',
        lightBg: 'bg-red-50',
        text: 'text-red-600',
        hover: 'hover:bg-red-100',
        border: 'border-red-200'
      },
      purple: {
        bg: 'bg-purple-500',
        lightBg: 'bg-purple-50',
        text: 'text-purple-600',
        hover: 'hover:bg-purple-100',
        border: 'border-purple-200'
      },
      indigo: {
        bg: 'bg-indigo-500',
        lightBg: 'bg-indigo-50',
        text: 'text-indigo-600',
        hover: 'hover:bg-indigo-100',
        border: 'border-indigo-200'
      },
      gray: {
        bg: 'bg-gray-500',
        lightBg: 'bg-gray-50',
        text: 'text-gray-600',
        hover: 'hover:bg-gray-100',
        border: 'border-gray-200'
      }
    };
    return colorMap[color];
  };

  const renderActionCard = (action: QuickAction) => {
    const colors = getColorClasses(action.color);

    const CardContent = (
      <div className={`relative p-6 bg-white border ${colors.border} rounded-lg shadow-sm ${colors.hover} transition-all duration-200 ${
        action.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'
      } group`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <div className={`p-2 rounded-lg ${colors.lightBg} mr-3`}>
                <action.icon className={`h-6 w-6 ${colors.text}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                  {action.label}
                </h3>
                {action.badge && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${colors.lightBg} ${colors.text}`}>
                    {action.badge}
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              {action.description}
            </p>
          </div>
        </div>
      </div>
    );

    if (action.disabled) {
      return <div key={action.id}>{CardContent}</div>;
    }

    if (action.href) {
      return (
        <Link key={action.id} to={action.href}>
          {CardContent}
        </Link>
      );
    }

    if (action.onClick) {
      return (
        <button
          key={action.id}
          onClick={action.onClick}
          className="w-full text-left"
        >
          {CardContent}
        </button>
      );
    }

    return <div key={action.id}>{CardContent}</div>;
  };

  return (
    <div className="mb-8">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">การดำเนินการด่วน</h2>
        <p className="text-sm text-gray-600">
          ฟังก์ชันที่ใช้บ่อยเพื่อจัดการระบบอย่างรวดเร็ว
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickActions.map((action) => renderActionCard(action))}
      </div>
    </div>
  );
};

export default QuickActions;